import { computed, inject, Injectable } from '@angular/core';
import { languages } from '@app/configurations';
import { TranslateService } from '@ngx-translate/core';
import { select } from '@ngxs/store';
import { de_DE, en_US, NzI18nService, zh_CN } from 'ng-zorro-antd/i18n';
import { getDefaultLanguage } from '../config/locale.config';
import { UserLanguage } from '../models/user.models';
import { RouterNavState } from '../store/states/router-nav/router-nav.state';
import { UserState } from '@app/core/store/states/user/user.state';
import { catchError, Observable, of, tap } from 'rxjs';
import { SignupService } from '@app/core/api/signup.service';

@Injectable({ providedIn: 'root' })
export class LocaleService {
  private i18n = inject(NzI18nService);

  private ts = inject(TranslateService);

  readonly sa = inject(SignupService);

  readonly availableLanguages = Object.values(languages).map((l) => l.toLowerCase());

  readonly userLocale = select(UserState.userLocale);

  readonly queryLanguageParam = select(RouterNavState.queryParam('lang'));

  readonly queryLanguage = computed(() => {
    const queryLanguage = this.queryLanguageParam()?.toLowerCase();

    return this.availableLanguages.includes(queryLanguage) ? queryLanguage : null;
  });

  readonly defaultLanguage = computed(
    () => this.queryLanguage() || this.sa.store?.data?.lang || this.ts.getBrowserLang(),
  );

  readonly language = computed<UserLanguage>(() => {
    return (this.queryLanguage() ||
      this.userLocale()?.toLowerCase() ||
      getDefaultLanguage(this.ts.getBrowserLang(), this.defaultLanguage()) ||
      this.defaultLanguage()) as UserLanguage;
  });

  initLanguage(): Observable<unknown> {
    this.ts.addLangs(this.availableLanguages);

    const defaultLanguage = getDefaultLanguage(this.ts.defaultLang);
    const language = this.language();

    if (defaultLanguage) {
      this.ts.setDefaultLang(defaultLanguage);
    }

    if (language) {
      return this.ts.use(language).pipe(
        tap(() => this.setAntLocale(language)),
        catchError((e) => {
          console.error(e);

          return of(null);
        }),
      );
    }
  }

  setAntLocale(lang: string) {
    switch (lang.toUpperCase()) {
      case languages.GERMAN:
        this.i18n.setLocale(de_DE);
        break;
      case languages.CHINESE:
        this.i18n.setLocale(zh_CN);
        break;
      case languages.ENGLISH:
        this.i18n.setLocale(en_US);
        break;
    }
  }
}
